import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { Action } from '@interstate/components/Action';
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { GAP } from '@makemydeal/dr-shared-types';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import {
    getFsCoxAutoCorrelationId,
    getFsNormalizedErrors,
    getFsRetryState,
    getFsSyncFailureViewModel
} from '@makemydeal/dr-dash-store/selectors/fsSyncFailureSelectors';
import { placeholderTextMapping } from '@makemydeal/dr-dash-store/utils/contractingUtils';
import type {
    FsBannerSeverity,
    FsFailureFieldLine,
    FsFailureGroup,
    NormalizedErrorCategory
} from '@makemydeal/dr-dash-store/types/fsSyncFailure';

import {
    FsSyncFailureAcknowledgement,
    FsSyncFailureBannerContainer,
    FsSyncFailureGroup,
    FsSyncFailureIndent,
    FsSyncFailureSection,
    FsSyncFailureTitle
} from './FsSyncFailureBanner.style';
import { FS_FEES_ENTITY, FS_UNROUTED_ENTITY, useFsEntitySlideout } from './useFsEntitySlideout';
import type { FsEntity } from './useFsEntitySlideout';
import { resolveFsFieldLabel } from './fsFieldLabelMap';
import { FS_GENERIC_REVIEW_LINE, resolveFsReasonCopy } from './fsReasonCopy';
import {
    FS_SYNC_FAILURE_ACKNOWLEDGEMENT as ACKNOWLEDGEMENT,
    FS_SYNC_FAILURE_ADMIN_OR_SUPPORT_BODY as ADMIN_OR_SUPPORT_BODY,
    FS_SYNC_FAILURE_AIO_INTERNAL_ERROR_TITLE as AIO_INTERNAL_ERROR_TITLE,
    FS_SYNC_FAILURE_BANNER_TITLE as BANNER_TITLE,
    FS_SYNC_FAILURE_CONFIGURATION_BODY as CONFIGURATION_BODY,
    FS_SYNC_FAILURE_CORRECTION_LINE as CORRECTION_LINE,
    FS_SYNC_FAILURE_FEES_CATCHALL_ALONE_LINE as FEES_CATCHALL_ALONE_LINE,
    FS_SYNC_FAILURE_FEES_CATCHALL_ALONGSIDE_LINE as FEES_CATCHALL_ALONGSIDE_LINE,
    FS_SYNC_FAILURE_INCOMPLETE_BUSINESS_SIGNER_TITLE as INCOMPLETE_BUSINESS_SIGNER_TITLE,
    FS_SYNC_FAILURE_MISSING_BUYER_TITLE as MISSING_BUYER_TITLE,
    FS_SYNC_FAILURE_ROLLUP_LINE as ROLLUP_LINE,
    FS_SYNC_FAILURE_SUPPORT_BODY as SUPPORT_BODY,
    FS_SYNC_FAILURE_TRANSIENT_EXHAUSTED as TRANSIENT_EXHAUSTED,
    FS_SYNC_FAILURE_TRANSIENT_EXHAUSTED_BODY as TRANSIENT_EXHAUSTED_BODY,
    FS_SYNC_FAILURE_TRANSIENT_RETRYING as TRANSIENT_RETRYING,
    FS_SYNC_FAILURE_TRANSIENT_RETRYING_BODY as TRANSIENT_RETRYING_BODY,
    FS_SYNC_FAILURE_UNCLASSIFIED_LINE as UNCLASSIFIED_LINE,
    FS_SYNC_FAILURE_UNCLASSIFIED_BODY as UNCLASSIFIED_BODY
} from '../constants';

const DISPLAYED_PAGE_ACTION = 'MV:FsSyncFailureDisplayed';
const DISMISSED_PAGE_ACTION = 'MV:FsSyncFailureDismissed';

const standaloneCopy = (
    category: NormalizedErrorCategory,
    severity: FsBannerSeverity,
    coxAutoCorrelationId?: string
): { title: string; body?: string; reference?: string } | undefined => {
    switch (category) {
        case 'aioInternalError':
            return { title: AIO_INTERNAL_ERROR_TITLE, body: CONFIGURATION_BODY };
        case 'configuration':
        case 'dealerConfig':
            return { title: BANNER_TITLE, body: SUPPORT_BODY, reference: coxAutoCorrelationId };
        case 'missingBuyer':
            return { title: MISSING_BUYER_TITLE, body: ADMIN_OR_SUPPORT_BODY };
        case 'incompleteBusinessSigner':
            return { title: INCOMPLETE_BUSINESS_SIGNER_TITLE, body: ADMIN_OR_SUPPORT_BODY };
        case 'transient':
            return severity === 'info'
                ? { title: TRANSIENT_RETRYING, body: TRANSIENT_RETRYING_BODY }
                : { title: TRANSIENT_EXHAUSTED, body: TRANSIENT_EXHAUSTED_BODY };
        case 'unknown':
            return { title: UNCLASSIFIED_LINE, body: UNCLASSIFIED_BODY };
        default:
            return undefined;
    }
};

const ENTITY_HEADING_LABEL: Partial<Record<FsEntity, string>> = {
    fees: 'Fees',
    protection: 'Protection Products',
    rate: 'Rate',
    taxes: 'Taxes',
    tradeIn: 'Trades'
};

const entityLabel = (entity: FsEntity): string | undefined =>
    ENTITY_HEADING_LABEL[entity] ?? (placeholderTextMapping[entity] as string | undefined);

const FEES_CATCHALL_FIELDS = ['frontendfees', 'otherfees'];

const fieldLabelOf = (line: FsFailureFieldLine): string | undefined => resolveFsFieldLabel(line.canonicalField)?.label;

const labelledFields = (group: FsFailureGroup) => group.fields.filter((line) => fieldLabelOf(line) !== undefined);

const sortedLabelledFields = (group: FsFailureGroup) =>
    labelledFields(group)
        .map((line) => ({ line, label: fieldLabelOf(line) }))
        .filter((pair): pair is { line: FsFailureFieldLine; label: string } => pair.label !== undefined)
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(({ line }) => line);

const hasFeesCatchAll = (group: FsFailureGroup) =>
    group.entity === FS_FEES_ENTITY && group.fields.some((line) => FEES_CATCHALL_FIELDS.includes(line.canonicalField));

const fieldLine = (line: FsFailureFieldLine, omitLabel: boolean) => {
    const label = fieldLabelOf(line);
    const reason = resolveFsReasonCopy(line.canonicalField);

    if (omitLabel && reason) {
        return (
            <Typography key={line.canonicalField} variant="body-sm" data-testid={`fs-sync-failure-field-${line.canonicalField}`}>
                {reason}
            </Typography>
        );
    }

    return (
        <Typography key={line.canonicalField} variant="body-sm" data-testid={`fs-sync-failure-field-${line.canonicalField}`}>
            <strong>{reason ? `${label}:` : label}</strong>
            {reason ? ` ${reason}` : null}
        </Typography>
    );
};

export const FsSyncFailureBanner = () => {
    const dispatch = useDispatch();
    const { category, severity, groups, hasGenericLine, errorCount } = useSelector(getFsSyncFailureViewModel);
    const normalizedErrors = useSelector(getFsNormalizedErrors);
    const retryState = useSelector(getFsRetryState);
    const coxAutoCorrelationId = useSelector(getFsCoxAutoCorrelationId);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const dealXgVersion = useSelector(offerReduxSelectors.getDealXgVersion);
    const isFnIDealMode = useSelector(dealModeSelectors.isFnIDealMode);
    const toggleEnabled = useSelector(featureToggleSelectors.isFsSyncFailureMessagingEnabled);
    const resolveSlideout = useFsEntitySlideout();

    const openerFor = (group: FsFailureGroup) => {
        const gapField = group.fields.find((line) => line.canonicalField === GAP);

        return resolveSlideout(group.entity, (gapField ?? group.fields[0])?.canonicalField);
    };

    const isNameable = (group: FsFailureGroup) => entityLabel(group.entity) !== undefined && openerFor(group) !== undefined;

    const rendersStandalone =
        category !== undefined && severity !== undefined && standaloneCopy(category, severity, coxAutoCorrelationId) !== undefined;

    const anythingUnaccounted = groups.some((group) => labelledFields(group).length < group.fields.length && !isNameable(group));

    const ownerLines = groups.reduce((total, group) => total + labelledFields(group).length + (isNameable(group) ? 1 : 0), 0);

    const renderedLineCount = rendersStandalone ? 0 : ownerLines + (hasGenericLine || anythingUnaccounted ? 1 : 0);

    const labelledCount = normalizedErrors.filter((error) => resolveFsFieldLabel(error.canonicalField) !== undefined).length;

    const telemetry = {
        dealXgId,
        dealXgVersion,
        coxAutoCorrelationId,
        attempt: retryState.attempt,
        maxAttempts: retryState.maxAttempts,
        category,
        severity,
        errorCount,
        labelledCount,
        unlabelledCount: normalizedErrors.length - labelledCount,
        entityGroups: groups.map((group) => group.entity).join(','),
        renderedLineCount,
        isFnIDealMode,
        toggleEnabled
    };

    useEffect(() => {
        if (category && severity) {
            logNewRelicPageAction(DISPLAYED_PAGE_ACTION, telemetry);
        }
    }, [category, severity, errorCount, coxAutoCorrelationId, retryState.attempt]);

    if (!category || !severity) {
        return null;
    }

    const acknowledge = () => {
        dispatch(baseDealActionCreators.dismissFsSyncFailure());
        logNewRelicPageAction(DISMISSED_PAGE_ACTION, telemetry);
    };

    const acknowledgement = (
        <FsSyncFailureAcknowledgement>
            <Action data-testid="fs-sync-failure-acknowledge" onClick={acknowledge}>
                {ACKNOWLEDGEMENT}
            </Action>
        </FsSyncFailureAcknowledgement>
    );

    const standalone = standaloneCopy(category, severity, coxAutoCorrelationId);

    if (standalone) {
        return (
            <FsSyncFailureBannerContainer>
                <Alert type={severity} data-testid="fs-sync-failure-banner" disableSuccessFade>
                    <FsSyncFailureTitle variant="h6">{standalone.title}</FsSyncFailureTitle>
                    {standalone.body && <Typography variant="body-sm">{standalone.body}</Typography>}
                    {standalone.reference && (
                        <Typography variant="body-sm">{`Support reference: ${standalone.reference}`}</Typography>
                    )}
                    {acknowledgement}
                </Alert>
            </FsSyncFailureBannerContainer>
        );
    }

    const entityAction = (group: FsFailureGroup) => {
        const open = openerFor(group);
        const name = entityLabel(group.entity);

        return open && name ? (
            <Action data-testid={`fs-sync-failure-entity-${group.entity}`} onClick={open}>
                {name}
            </Action>
        ) : null;
    };

    const rollupLine = (group: FsFailureGroup) => (
        <Typography variant="body-sm" data-testid={`fs-sync-failure-rollup-${group.entity}`}>
            {ROLLUP_LINE}
        </Typography>
    );

    const feesCatchAllLine = (hasNamedField: boolean) => (
        <Typography key="fees-catchall" variant="body-sm" data-testid="fs-sync-failure-field-fees-catchall">
            {hasNamedField ? FEES_CATCHALL_ALONGSIDE_LINE : FEES_CATCHALL_ALONE_LINE}
        </Typography>
    );

    const genericReviewLine = <Typography variant="body-sm">{FS_GENERIC_REVIEW_LINE}</Typography>;

    return (
        <FsSyncFailureBannerContainer>
            <Alert type={severity} data-testid="fs-sync-failure-banner" disableSuccessFade>
                <FsSyncFailureTitle variant="h6">{BANNER_TITLE}</FsSyncFailureTitle>
                {ownerLines > 0 && <Typography variant="body-sm">{CORRECTION_LINE}</Typography>}

                {groups
                    .filter((group) => isNameable(group) || labelledFields(group).length > 0)
                    .map((group) => {
                        if (group.entity === FS_UNROUTED_ENTITY) {
                            return (
                                <FsSyncFailureSection key={group.entity}>
                                    {sortedLabelledFields(group).map((line) => fieldLine(line, false))}
                                </FsSyncFailureSection>
                            );
                        }

                        if (labelledFields(group).length === 0 && !hasFeesCatchAll(group)) {
                            return (
                                <FsSyncFailureGroup key={group.entity}>
                                    {entityAction(group)}
                                    <FsSyncFailureIndent>{rollupLine(group)}</FsSyncFailureIndent>
                                </FsSyncFailureGroup>
                            );
                        }

                        const fields = sortedLabelledFields(group);
                        const omitLabel = fields.length === 1 && fieldLabelOf(fields[0]) === entityLabel(group.entity);
                        const fieldLines: ReactNode[] = fields.map((line) => fieldLine(line, omitLabel));

                        if (hasFeesCatchAll(group)) {
                            fieldLines.push(feesCatchAllLine(fields.length > 0));
                        }

                        return (
                            <FsSyncFailureGroup key={group.entity}>
                                {entityAction(group)}
                                <FsSyncFailureIndent>{fieldLines}</FsSyncFailureIndent>
                            </FsSyncFailureGroup>
                        );
                    })}

                {(hasGenericLine || anythingUnaccounted) &&
                    (ownerLines > 0 ? <FsSyncFailureIndent>{genericReviewLine}</FsSyncFailureIndent> : genericReviewLine)}

                {acknowledgement}
            </Alert>
        </FsSyncFailureBannerContainer>
    );
};
